import classNames from 'classnames';
import React from 'react';

import { Button } from './Button';
import { PopupMinimizedPosition } from './contentTypes';
import { PackedModalProps } from './Modal';
import { useModalsContext } from './ModalsContext';
import { useModals } from './useModals';
import { uiClassName } from './util';

const DEFAULT_POSITION = 'bottom-left';
const DEFAULT_BORDER_RADIUS = '10px 10px 0 0';
const Z_INDEX = 10;

const BUTTON_STYLES: Record<PopupMinimizedPosition, React.CSSProperties> = {
  bottom: { borderRadius: DEFAULT_BORDER_RADIUS },
  'bottom-left': { borderRadius: DEFAULT_BORDER_RADIUS },
  'bottom-right': { borderRadius: DEFAULT_BORDER_RADIUS },
  left: {
    borderRadius: DEFAULT_BORDER_RADIUS,
    transform: 'rotate(90deg) translateY(-50%) translateX(-50%)',
    transformOrigin: 'left center',
  },
  'left-bottom': {
    borderRadius: DEFAULT_BORDER_RADIUS,
    transform: 'rotate(90deg) translateX(-100%)',
    transformOrigin: 'left bottom',
  },
  right: {
    borderRadius: DEFAULT_BORDER_RADIUS,
    transform: 'rotate(-90deg) translateY(-50%) translateX(50%)',
    transformOrigin: 'right center',
  },
  'right-bottom': {
    borderRadius: DEFAULT_BORDER_RADIUS,
    transform: 'rotate(-90deg) translateX(100%)',
    transformOrigin: 'right bottom',
  },
};

const POSITION_BS5_CLASSES: Record<PopupMinimizedPosition, string> = {
  bottom: 'bottom-0 justify-content-center w-100',
  'bottom-left': 'bottom-0 justify-content-start w-100',
  'bottom-right': 'bottom-0 justify-content-end w-100',
  left: 'left-0 align-items-center h-100',
  'left-bottom': 'left-0 align-items-end h-100',
  right: 'end-0 align-items-center h-100',
  'right-bottom': 'end-0 align-items-end h-100',
};

const BORDER_SPACE_BY_POSITION: Record<PopupMinimizedPosition, React.CSSProperties | undefined> = {
  bottom: undefined,
  'bottom-left': { paddingLeft: 80 },
  'bottom-right': { paddingRight: 80 },
  left: undefined,
  'left-bottom': { paddingBottom: 80 },
  right: undefined,
  'right-bottom': { paddingBottom: 80 },
};

const getButtonStyle = (position?: PopupMinimizedPosition): React.CSSProperties => {
  if (!position || !(position in BUTTON_STYLES)) {
    return { borderRadius: DEFAULT_BORDER_RADIUS };
  }
  return BUTTON_STYLES[position];
};

const getPositionClassName = (position: PopupMinimizedPosition): string => {
  return POSITION_BS5_CLASSES[position] || POSITION_BS5_CLASSES[DEFAULT_POSITION];
};

const getSpaceFromBorderByPosition = (position: PopupMinimizedPosition) => {
  return BORDER_SPACE_BY_POSITION[position];
};

const ButtonMinimized = ({ id, minimizedProps }: PackedModalProps) => {
  const { maximize } = useModals();

  const handleMaximize = React.useCallback(() => {
    maximize(id);
  }, [maximize, id]);

  const buttonStyle = React.useMemo(
    (): React.CSSProperties => ({
      ...getButtonStyle(minimizedProps?.position),
      pointerEvents: 'auto',
    }),
    [minimizedProps?.position]
  );

  return (
    <Button key={id} onClick={handleMaximize} style={buttonStyle} variant={minimizedProps?.variant}>
      {minimizedProps?.title}
    </Button>
  );
};

export const ModalMinimized = () => {
  const { modals } = useModalsContext();

  const minimizedModalsGroupedByPosition = React.useMemo(() => {
    const minimizedModals = modals.filter((m) => m.isMinimized);
    return minimizedModals.reduce((acc, modal) => {
      const position = modal.minimizedProps?.position || 'bottom-left';
      acc[position] = [...(acc[position] || []), modal];
      return acc;
    }, {} as Record<PopupMinimizedPosition, Array<PackedModalProps> | undefined>);
  }, [modals]);

  if (Object.keys(minimizedModalsGroupedByPosition).length === 0) return null;

  return (
    <>
      {Object.entries(minimizedModalsGroupedByPosition).map(([position, minimizedModals]) => (
        <div
          className={uiClassName({
            bs5: classNames(getPositionClassName(position as PopupMinimizedPosition), 'position-fixed d-flex gap-2'),
          })}
          key={position}
          style={{
            pointerEvents: 'none',
            zIndex: Z_INDEX,
            ...getSpaceFromBorderByPosition(position as PopupMinimizedPosition),
          }}
        >
          {minimizedModals?.map((modal) => (
            <ButtonMinimized key={modal.id} {...modal} />
          ))}
        </div>
      ))}
    </>
  );
};
